/**
 * The text tool's own controls (SRS C4), shown while it is in hand.
 *
 * Size, bold, italic and how the words sit in their box. Changing any of them sets up
 * the next box, and changes the box being typed into if one is open — so a teacher can
 * write a sentence and then make it bold, which is the order people actually work in.
 *
 * The colour comes from the same palette the pen uses, because writing is ink too.
 */

import type { TextAlign } from '@opal/shared';

import { MAX_FONT_SIZE, MIN_FONT_SIZE } from '../../board/text-object.js';
import { useBoardStore } from '../../board/store.js';
import { focusWritingField } from './TextEditor.js';

const ALIGNMENTS: readonly { value: TextAlign; label: string; lines: readonly string[] }[] = [
  { value: 'left', label: 'Left', lines: ['M4 7h16', 'M4 12h10', 'M4 17h13'] },
  { value: 'center', label: 'Centre', lines: ['M4 7h16', 'M7 12h10', 'M5.5 17h13'] },
  { value: 'right', label: 'Right', lines: ['M4 7h16', 'M10 12h10', 'M7 17h13'] },
];

/** Keeps the caret in the words being typed, so a control can be used mid-sentence. */
function keepCaret(event: { preventDefault: () => void }): void {
  event.preventDefault();
}

const BUTTON =
  'flex min-h-touch flex-1 items-center justify-center gap-1 rounded-md border px-2 py-2 text-sm transition-colors duration-fast';
const ON = 'border-primary bg-primary-subtle text-primary';
const OFF = 'border-border bg-surface text-text-primary hover:bg-surface-hover';

/** How big new writing is, in canvas units — the same units the pen is set in. */
function SizeSlider() {
  const fontSize = useBoardStore((state) => state.fontSize);
  const setFontSize = useBoardStore((state) => state.setFontSize);

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between">
        <h3 className="text-sm font-medium text-text-secondary">Size</h3>
        <span className="font-mono text-sm text-text-primary">{fontSize}</span>
      </div>
      <input
        type="range"
        min={MIN_FONT_SIZE}
        max={MAX_FONT_SIZE}
        step={1}
        value={fontSize}
        aria-label="Text size"
        onChange={(event) => {
          setFontSize(Number(event.target.value));
        }}
        onPointerUp={focusWritingField}
        className="w-full accent-primary"
      />
    </section>
  );
}

/** Bold and italic, each a switch that stays on until it is turned off. */
function StylePicker() {
  const bold = useBoardStore((state) => state.bold);
  const italic = useBoardStore((state) => state.italic);
  const setBold = useBoardStore((state) => state.setBold);
  const setItalic = useBoardStore((state) => state.setItalic);

  return (
    <div className="flex gap-2">
      <button
        type="button"
        aria-pressed={bold}
        onMouseDown={keepCaret}
        onClick={() => {
          setBold(!bold);
        }}
        className={`${BUTTON} font-bold ${bold ? ON : OFF}`}
      >
        B
      </button>
      <button
        type="button"
        aria-pressed={italic}
        onMouseDown={keepCaret}
        onClick={() => {
          setItalic(!italic);
        }}
        className={`${BUTTON} italic ${italic ? ON : OFF}`}
      >
        I
      </button>
    </div>
  );
}

/** Where the words sit inside their box: left, centred or right. */
function AlignPicker() {
  const align = useBoardStore((state) => state.align);
  const setAlign = useBoardStore((state) => state.setAlign);

  return (
    <div className="flex gap-2">
      {ALIGNMENTS.map(({ value, label, lines }) => (
        <button
          key={value}
          type="button"
          aria-pressed={align === value}
          aria-label={label}
          title={label}
          onMouseDown={keepCaret}
          onClick={() => {
            setAlign(value);
          }}
          className={`${BUTTON} ${align === value ? ON : OFF}`}
        >
          <svg
            width={20}
            height={20}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            strokeLinecap="round"
            aria-hidden
          >
            {lines.map((path) => (
              <path key={path} d={path} />
            ))}
          </svg>
        </button>
      ))}
    </div>
  );
}

export function TextControls() {
  return (
    <>
      <section className="flex flex-col gap-2">
        <h3 className="text-sm font-medium text-text-secondary">Writing</h3>
        <StylePicker />
        <AlignPicker />
        <p className="text-xs text-text-secondary">
          Tap the board to start writing, or tap words already there to change them.
          Tap away when you are done.
        </p>
      </section>
      <SizeSlider />
    </>
  );
}
